
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchTable.css';

const COLUMNS_CONFIG = [
  { key: 'id_del_proceso', label: 'ID del Proceso' },
  { key: 'entidad', label: 'Entidad' },
  { key: 'nit_entidad', label: 'NIT Entidad' },
  { key: 'departamento_entidad', label: 'Departamento' },
  { key: 'ciudad_entidad', label: 'Ciudad' },
  { key: 'nombre_del_procedimiento', label: 'Procedimiento' },
  { key: 'descripcion_del_procedimiento', label: 'Descripción' },
  { key: 'fecha_de_publicacion_del_proceso', label: 'Fecha Publicación', format: 'date' },
  { key: 'precio_base', label: 'Precio Base', format: 'currency' },
  { key: 'duracion', label: 'Duración' },
  { key: 'unidad_de_duracion', label: 'Unidad' },
  { key: 'adjudicado', label: 'Adjudicado' },
  { key: 'valor_total_adjudicacion', label: 'Valor Adjudicación', format: 'currency' },
  { key: 'nombre_del_proveedor_adjudicado', label: 'Proveedor Adjudicado' },
  { key: 'nit_del_proveedor_adjudicado', label: 'NIT Proveedor' },
  { key: 'codigo_principal_de_categoria', label: 'Código Categoría' },
  { key: 'categorias_adicionales', label: 'Categorías Adicionales' },
  { key: 'urlproceso', label: 'Enlace', format: 'link' }
];

function formatFilterDateValue(value) {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();
  if (!raw) {
    return "";
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const first = Number(slashMatch[1]);
    const second = Number(slashMatch[2]);
    const year = slashMatch[3];

    if (first > 12 && second <= 12) {
      return `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${year}`;
    }

    if (second > 12 && first <= 12) {
      return `${String(second).padStart(2, '0')}/${String(first).padStart(2, '0')}/${year}`;
    }

    return `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${year}`;
  }

  return raw;
}

function formatActiveFilterValue(key, value) {
  if (key === 'fecha_desde' || key === 'fecha_hasta') {
    return formatFilterDateValue(value);
  }

  if (Array.isArray(value)) {
    return value.join(" | ");
  }

  return String(value);
}

function SearchTable(props) {
  const {
    data,
    onFilter,
    paginationInfo,
    onPageChange,
    searchQuery = '',
    onSearchQueryChange,
    onLoadFilter,
    activeFilters = {},
    onRemoveFilter,
    onOpenSaveFilter,
  } = props;

  const navigate = useNavigate();
  const hasRows = Array.isArray(data) && data.length > 0;

  // Filtrar solo los filtros con valores no vacíos/definidos
  const activeEntries = Object.entries(activeFilters || {}).filter(([_, v]) => {
    if (v === undefined || v === null) return false;
    const s = String(v);
    return s.trim() !== '';
  });
  const hasActive = activeEntries.length > 0;

  // Usar estado local si no se proporciona onSearchQueryChange
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastUpdateHuman, setLastUpdateHuman] = useState(null);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertPasscode, setRevertPasscode] = useState('');
  const [reverting, setReverting] = useState(false);
  const [revertMsg, setRevertMsg] = useState(null);
  const [revertErr, setRevertErr] = useState(null);
  
  // Estados para análisis
  const [startingAnalysis, setStartingAnalysis] = useState(false);
  const [analysisMsg, setAnalysisMsg] = useState(null);
  const [analysisErr, setAnalysisErr] = useState(null);

  useEffect(() => {
    const loadLastUpdate = async () => {
      try {
        const res = await fetch('/api/analytics/last-update/');
        if (res.ok) {
          const json = await res.json();
          setLastUpdate(json.last_update || null);
          setLastUpdateHuman(json.last_update_human || null);
        }
      } catch (e) {
        // ignorar
      }
    };
    loadLastUpdate();
  }, []);
  
  // Usar el valor controlado si se proporciona onSearchQueryChange
  const actualSearchQuery = onSearchQueryChange ? searchQuery : localSearchQuery;
  
  // Handler para cambios en la barra de búsqueda
  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    if (onSearchQueryChange) {
      onSearchQueryChange(newValue);
    } else {
      setLocalSearchQuery(newValue);
    }
  };

  // Handler para búsqueda global al presionar Enter o botón
  const handleGlobalSearch = (e) => {
    if ((e.key === "Enter" || e.type === "click") && onFilter) {
      onFilter({ q: actualSearchQuery.trim() });
    }
  };

  const handleExportCsv = () => {
    window.location.href = '/api/analytics/licitaciones/export/';
  };

  // Función para obtener el token CSRF
  const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  // Handler para iniciar análisis
  const handleStartAnalysis = async () => {
    // Verificar si ya existe una sesión activa
    const existingSession = sessionStorage.getItem('analysisSession');
    let previousSessionId = null;
    if (existingSession) {
      try {
        previousSessionId = JSON.parse(existingSession)?.sessionId || null;
      } catch (error) {
        previousSessionId = null;
      }
    }
    if (existingSession) {
      const confirm = window.confirm(
        'Ya existe un análisis activo.\n\n' +
        'Si continúa, se creará un nuevo análisis con los filtros actuales y reemplazará el análisis anterior.\n\n' +
        '¿Desea continuar?'
      );
      if (!confirm) {
        return; // Usuario canceló
      }
    }
    
    setStartingAnalysis(true);
    setAnalysisMsg(null);
    setAnalysisErr(null);
    
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/analytics/start-analysis/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          filters: activeFilters
        })
      });
      
      // Intentar parsear JSON solo si la respuesta tiene contenido
      let result;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.error('Respuesta no-JSON:', text);
        throw new Error('El servidor no devolvió JSON válido');
      }
      
      if (response.ok && result.success) {
        setAnalysisMsg(result.message);
        
        // Limpiar cualquier sesión anterior antes de guardar la nueva
        sessionStorage.removeItem('analysisSession');
        if (previousSessionId) {
          sessionStorage.removeItem(`analysisReport:${previousSessionId}`);
        }
        
        // Guardar la nueva sesión en sessionStorage para persistencia
        const sessionData = {
          sessionId: result.session_id,
          recordCount: result.record_count,
          createdAt: new Date().toISOString(),
          filters: activeFilters
        };
        sessionStorage.setItem('analysisSession', JSON.stringify(sessionData));
        
        // Redirigir al dashboard con la nueva sesión
        navigate('/dashboard');
      } else {
        const errorMsg = result.error || `Error ${response.status}: ${response.statusText}`;
        setAnalysisErr(errorMsg);
        alert(`✗ Error: ${errorMsg}`);
      }
    } catch (error) {
      const errorMsg = 'Error de conexión: ' + error.message;
      setAnalysisErr(errorMsg);
      console.error('Error completo:', error);
      alert(`✗ ${errorMsg}`);
    } finally {
      setStartingAnalysis(false);
    }
  };
  
  // Handlers de paginación
  const handlePreviousPage = () => {
    if (paginationInfo?.has_previous && onPageChange) {
      onPageChange(paginationInfo.current_page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo?.has_next && onPageChange) {
      onPageChange(paginationInfo.current_page + 1);
    }
  };

  // Mostrar barra de búsqueda y botones siempre, aunque no haya datos
  const renderSearchBar = () => (
    <>
      <div className="search-bar-actions-row">
        <div className="search-bar-container" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="search-input-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              className="search-icon-button"
              onClick={handleGlobalSearch}
              title="Buscar"
            >
              <svg 
                className="search-icon-svg" 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar"
              value={actualSearchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={handleGlobalSearch}
            />
          </div>
        </div>
        {onFilter && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            {onLoadFilter && (
              <button
                className="btn btn-neutral filter-btn"
                onClick={onLoadFilter}
                style={{ height: '38px', padding: '0 1.2rem' }}
              >
                Cargar Filtro
              </button>
            )}
            <button
              type="button"
              className="btn btn-neutral filter-btn export-btn"
              onClick={handleExportCsv}
              style={{ height: '38px', padding: '0 1.2rem' }}
            >
              Exportar
            </button>
            <button className="btn btn-primary filter-btn" onClick={() => onFilter()} style={{ height: '38px', padding: '0 1.2rem' }}>
              Filtrar
            </button>
          </div>
        )}
        </div>
      </div>
      {/* Filtros activos */}
      {hasActive && (
        <>
          <div className="active-filters-header">
            <div className="active-filters-title">FILTROS ACTIVOS</div>
            {onOpenSaveFilter && (
              <button 
                className="btn btn-primary filter-btn"
                onClick={onOpenSaveFilter}
                style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
              >
                Guardar Filtro
              </button>
            )}
          </div>
          <div className="active-filters-container table-width">
            <ul className="active-filters-list">
              {activeEntries.map(([key, value]) => {
                const label = (key.replace(/_/g, ' ')).replace(/\b\w/g, l => l.toUpperCase());
                const displayValue = formatActiveFilterValue(key, value);
                return (
                  <li key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="filter-key">{label}:</span> <span className="filter-value">{displayValue}</span>
                    {onRemoveFilter && (
                      <button
                        className="remove-filter-btn"
                        title="Eliminar filtro"
                        onClick={() => onRemoveFilter(key)}
                        style={{ marginLeft: '0.5rem', cursor: 'pointer', border: 'none', background: 'transparent', color: '#c00', fontWeight: 'bold', fontSize: '1rem' }}
                      >
                        ×
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          {/* Botón Iniciar Análisis */}
          {hasRows && (
            <div className="start-analysis-btn-container">
              <button 
                className={`start-analysis-btn ${startingAnalysis ? 'start-analysis-btn-loading' : ''}`}
                onClick={handleStartAnalysis}
                disabled={startingAnalysis}
              >
                <span className="start-analysis-btn-icon">
                  {startingAnalysis ? (
                    <svg className="start-analysis-spinner" width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="15">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                      </circle>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 11 12 8 15 11"/>
                      <polyline points="9 17 12 14 15 17"/>
                    </svg>
                  )}
                </span>
                <span className="start-analysis-btn-text">
                  {startingAnalysis ? 'Iniciando Análisis...' : 'Iniciar Análisis'}
                </span>
                {!startingAnalysis && (
                  <span className="start-analysis-btn-glow"></span>
                )}
              </button>
            </div>
          )}
        </>
      )}
      {(lastUpdateHuman || lastUpdate) && (
        <div className="last-update-text">
          Última actualización de los datos: {lastUpdateHuman || lastUpdate}
          <button
            type="button"
            className="link-button"
            onClick={() => { setShowRevertModal(true); setRevertMsg(null); setRevertErr(null); setRevertPasscode(''); }}
            title="Revertir última carga"
          >
            ¿Notas algún problema con la última carga?
          </button>
        </div>
      )}
    </>
  );

  const revertModal = showRevertModal ? (
    <div className="modal-overlay revert-modal" onClick={() => setShowRevertModal(false)}>
      <div className="modal-content revert-modal-content" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Revertir carga</h3>
        <p>Ingresa la clave para revertir la última carga. Esta acción eliminará los registros creados en la última recuperación del SECOP II.</p>
        <label htmlFor="revert-passcode" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Clave</label>
        <input
          id="revert-passcode"
          type="password"
          className="revert-passcode-input"
          value={revertPasscode}
          onChange={(e) => setRevertPasscode(e.target.value)}
          placeholder="000000"
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '0.75rem' }}
        />
        {revertMsg && <div style={{ color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb', padding: '0.5rem', borderRadius: 4, marginBottom: '0.75rem' }}>{revertMsg}</div>}
        {revertErr && <div style={{ color: '#721c24', background: '#f8d7da', border: '1px solid #f5c6cb', padding: '0.5rem', borderRadius: 4, marginBottom: '0.75rem' }}>{revertErr}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setShowRevertModal(false)} disabled={reverting}>Cancelar</button>
          <button
            className="btn btn-danger"
            disabled={reverting || !revertPasscode}
            onClick={async () => {
              setReverting(true);
              setRevertErr(null);
              setRevertMsg(null);
              try {
                const res = await fetch('/api/analytics/revert-last-fetch/', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ passcode: revertPasscode, minutes_window: 10 })
                });
                const json = await res.json();
                if (!res.ok || json.success === false) {
                  throw new Error(json.error || 'No se pudo revertir la carga');
                }
                setRevertMsg(`Se eliminaron ${json.deleted || 0} registros.`);
              } catch (err) {
                setRevertErr(err.message);
              } finally {
                setReverting(false);
              }
            }}
            title="Esta acción eliminará registros de la última carga"
          >
            {reverting ? 'Revirtiendo...' : 'Revertir carga'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (!data || data.length === 0) {
    return (
      <>
        <div className="search-table-container">
          {renderSearchBar()}
          <div className="empty-results-state">
            <div className="empty-results-icon" aria-hidden="true">
              <svg viewBox="0 0 64 64" fill="none">
                <circle cx="28" cy="28" r="16" stroke="currentColor" strokeWidth="3.5" />
                <path d="M39.5 39.5L52 52" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                <path d="M22 28H34" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                <path d="M28 22V34" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="empty-results-title">No hay datos que coincidan con la búsqueda</h3>
            <p className="empty-results-text">
              Ajusta los filtros o cambia el término de búsqueda para intentar nuevamente.
            </p>
          </div>
        </div>
        {revertModal}
      </>
    );
  }

  const formatValue = (value, format) => {
    if (value == null) return '';
    switch (format) {
      case 'currency':
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return value;
        return numValue.toLocaleString('es-CO', {
          style: 'currency',
          currency: 'COP',
          maximumFractionDigits: 0
        });
      case 'date':
        // Formato de fecha: DD/MM/YYYY - evitar problemas de zona horaria
        if (!value) return '';
        try {
          // Si el valor está en formato YYYY-MM-DD, convertir a DD/MM/YYYY
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = value.split('-');
            return `${day}/${month}/${year}`;
          }
          // Si ya está en formato DD/MM/YYYY o otro formato, devolverlo tal como está
          return value;
        } catch (e) {
          return value;
        }
      case 'link':
        if (!value) return '';
        try {
          let url = String(value).trim();
          
          // Si el valor tiene formato {'url': '...'}, extraer la URL
          if (url.includes("{'url':") || url.includes('{"url":')) {
            const match = url.match(/['"]url['"]\s*:\s*['"]([^'"]+)['"]/i);
            if (match && match[1]) {
              url = match[1];
            }
          }
          
          // Asegurar que la URL comience con http:// o https://
          if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
          }
          
          if (url) {
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="table-link"
                onClick={(e) => e.stopPropagation()}
              >
                Ver detalle
              </a>
            );
          }
        } catch (e) {
          console.error("Error en formatValue para link:", e);
        }
        return '';
      default:
        const str = String(value || '');
        return str.length > 80 ? `${str.substring(0, 77)}...` : str;
    }
  };

  return (
    <>
      <div className="search-table-container">
        {renderSearchBar()}
        
        <div className="table-wrapper">
          <div className="table-responsive">
            <table className="search-table">
              <thead>
                <tr>
                  {COLUMNS_CONFIG.map(col => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={row.id_del_proceso || idx}>
                    {COLUMNS_CONFIG.map(col => {
                      const rawValue = row[col.key];
                      const formattedValue = formatValue(rawValue, col.format);
                      const showTooltip = col.format !== 'link' && col.format !== 'currency' && col.format !== 'date' && rawValue && String(rawValue).length > 80;
                      
                      return (
                        <td 
                          key={col.key}
                          title={showTooltip ? String(rawValue) : ''}
                        >
                          {formattedValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Footer de paginación */}
          {paginationInfo && (
            <div className="pagination-footer">
              <div className="pagination-controls">
                <button
                  onClick={handlePreviousPage}
                  disabled={!paginationInfo.has_previous}
                  className="pagination-btn"
                  title="Página anterior"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15,18 9,12 15,6"></polyline>
                  </svg>
                </button>
                
                <span className="pagination-current">
                  {paginationInfo.current_page}
                </span>
                
                <button
                  onClick={handleNextPage}
                  disabled={!paginationInfo.has_next}
                  className="pagination-btn"
                  title="Página siguiente"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9,18 15,12 9,6"></polyline>
                  </svg>
                </button>
              </div>
              
              <div className="pagination-info">
                <div className="pagination-stats">
                  <span className="pagination-count">
                    Mostrando {((paginationInfo.current_page - 1) * paginationInfo.limit) + 1}-{Math.min(paginationInfo.current_page * paginationInfo.limit, paginationInfo.count)} de {paginationInfo.count.toLocaleString()} registros
                  </span>
                  <span className="pagination-pages">
                    Página {paginationInfo.current_page} de {paginationInfo.total_pages}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {revertModal}
    </>
  );
}

export default SearchTable;
